import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../contexts/AuthProvider';
import EmpresasService from '../../services/EmpresasService';
import { IEmpresas } from '../../interfaces/IEmpresas';
import workflowService from './workflowService';
import EditarComposicaoGrupoModal from './EditarComposicaoGrupoModal';
import { calcularCobertura } from './coberturaDePara';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { justificativaDeParaIndividual, justificativaDeParaLote, justificativaRenomearGrupo } from './justificativaSox';
import { contarVinculosDaRegra, DICA_REGRA, ehVinculoDaRegra, ROTULO_REGRA } from './origemVinculo';
import { agruparPorSubgrupo, opcoesDeSubgrupo } from './subgruposDePara';
import { ehContaDeDepreciacao, GRUPO_DEPRECIACAO, opcoesDeGrupo } from './gruposPredefinidos';
import type { EmpresaTenant, MatrizContaContabil } from './models';

interface Periodo {
  esteiraId: number;
  periodoInicio: string;
  periodoFim: string;
  ativo: boolean;
  label: string;
}

interface DeParaItem {
  grupoContaId: number;
  tenantId: number;
  grupoNome: string;
  subgrupoNome: string | null;
  contaId: number;
  contaCodigo: string;
  contaDescricao: string;
  criadoEm: string;
  origem?: 'USUARIO' | 'REGRA';
}

interface GrupoNome {
  grupoNome: string;
  subgrupoNome: string | null;
}

// Grupos de conta (derivados do ECD) que identificam contas de Clientes — mesma lista usada no
// backend (WfDeParaTipoGrupo.GruposDeCliente).
const GRUPOS_DE_CLIENTE = ['Somente Cliente', 'Cliente/Fornecedor'];
const ehContaDeCliente = (grupoConta: string | null | undefined) =>
  !!grupoConta && GRUPOS_DE_CLIENTE.includes(grupoConta);

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const CadastroDeParaPage: React.FC = () => {
  const history = useHistory();
  const { usuario } = useAuth();
  const userId = usuario?.id ?? 0;

  const [empresas, setEmpresas] = useState<IEmpresas[]>([]);
  const [tenants, setTenants] = useState<EmpresaTenant[]>([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState<IEmpresas | null>(null);

  const [periodos, setPeriodos] = useState<Periodo[]>([]);
  const [selectedPeriodo, setSelectedPeriodo] = useState<Periodo | null>(null);

  const [contas, setContas] = useState<MatrizContaContabil[]>([]);
  const [mapeamentos, setMapeamentos] = useState<DeParaItem[]>([]);
  const [gruposExistentes, setGruposExistentes] = useState<GrupoNome[]>([]);
  const [loading, setLoading] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState('');
  const [syncError, setSyncError] = useState('');

  // Filtros
  const [busca, setBusca] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState(''); // grupo_conta do ECD
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'mapeado' | 'pendente'>('todos');
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'ANALITICA' | 'SINTETICA'>('todos');

  // Form individual
  const [contaSelecionada, setContaSelecionada] = useState<MatrizContaContabil | null>(null);
  const [grupoNome, setGrupoNome] = useState('');
  const [subgrupoNome, setSubgrupoNome] = useState('');
  // Justificativa SOX já sugerida (conta + grupo/subgrupo); acompanha as escolhas até a pessoa editar o texto.
  const justificativa = useJustificativaSugerida(
    contaSelecionada
      ? justificativaDeParaIndividual({ conta: { codigo: contaSelecionada.codigo, descricao: contaSelecionada.descricao }, grupo: grupoNome, subgrupo: subgrupoNome })
      : '');
  const [showForm, setShowForm] = useState(false);

  const [pagina, setPagina] = useState(0);

  // Form em lote
  const [showLote, setShowLote] = useState(false);
  const [grupoNomeLote, setGrupoNomeLote] = useState('');
  const [subgrupoNomeLote, setSubgrupoNomeLote] = useState('');
  const [salvandoLote, setSalvandoLote] = useState(false);
  // Tipo de grupo: 'CLIENTES' lista/permite somente contas de Clientes
  const [tipoGrupo, setTipoGrupo] = useState<'QUALQUER' | 'CLIENTES'>('QUALQUER');
  const [selecionadas, setSelecionadas] = useState<Set<number>>(new Set());
  // Sugestão do lote: cita o grupo/subgrupo digitados e as contas marcadas (e a categoria delas no ECD).
  const justificativaLote = useJustificativaSugerida(
    justificativaDeParaLote({
      contas: contas.filter(c => selecionadas.has(c.contaId)).map(c => ({ codigo: c.codigo, descricao: c.descricao, grupoConta: c.grupoConta })),
      grupo: grupoNomeLote, subgrupo: subgrupoNomeLote, apenasClientes: tipoGrupo === 'CLIENTES',
    }));
  const [gruposAbertos, setGruposAbertos] = useState<Set<string>>(new Set());
  // Grupo cuja composição de contas está sendo editada (modal)
  const [grupoEmEdicao, setGrupoEmEdicao] = useState<string | null>(null);
  const [salvandoComposicao, setSalvandoComposicao] = useState(false);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 5000);
  };

  // Carrega empresas ao montar
  useEffect(() => {
    const cid = usuario?.clienteID;
    if (!cid) return;
    EmpresasService.obterTodosPorClienteID(cid)
      .then(lista => setEmpresas(lista))
      .catch(() => {});
    workflowService.listarTenants()
      .then(lista => setTenants(lista))
      .catch(() => setTenants([]));
  }, [usuario?.clienteID]);

  // Só empresas cadastradas no grupo econômico do Workflow SOX (mesmo critério usado no
  // Mapa de Conciliação — andamentoContas/board.tsx) podem ter contas mapeadas De/Para.
  const empresasConfiguradas = empresas.filter(e =>
    tenants.some(t => t.empresaId === e.id && t.grupoId != null && t.status === 'ATIVA')
  );

  const selecionarEmpresa = (emp: IEmpresas) => {
    setSelectedEmpresa(emp);
    setSelectedPeriodo(null);
    setContas([]);
    setMapeamentos([]);
    setGruposExistentes([]);
    setSyncError('');
    setPagina(0);
    setSelecionadas(new Set());
    limparFiltros();

    workflowService.getPeriodosEmpresa(emp.id)
      .then(lista => setPeriodos(lista))
      .catch(() => setPeriodos([]));
  };

  const selecionarPeriodo = useCallback((emp: IEmpresas, periodo: Periodo) => {
    setSelectedPeriodo(periodo);
    carregarContas(emp.id, periodo.esteiraId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Contas de resultado não são elegíveis ao De/Para: o backend as omite (também nas buscas) e
  // os mapeamentos antigos delas são ignorados para não inflar os indicadores nem a lista de grupos.
  // Toda carga passa por aqui, então a regra vale ao trocar empresa/período e ao atualizar o ECD.
  const carregarDadosDeMapeamento = async (tenantId: number) => {
    const [c, m, g] = await Promise.all([
      workflowService.matrizContas(tenantId, undefined, true),
      workflowService.deParaListar(tenantId),
      workflowService.deParaListarGrupos(tenantId),
    ]);
    const idsElegiveis = new Set(c.map(x => x.contaId));
    setContas(c);
    setMapeamentos(m.filter(x => idsElegiveis.has(x.contaId)));
    setGruposExistentes(g);
    return { contas: c };
  };

  const carregarContas = async (tenantId: number, esteiraId: number) => {
    setSyncError('');
    setLoading(true);
    try {
      const { contas: c } = await carregarDadosDeMapeamento(tenantId);

      if (c.length === 0) {
        await sincronizarDoEcd(tenantId, esteiraId, true);
      }
    } catch {
      setSyncError('Erro ao carregar as contas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const sincronizarDoEcd = async (tenantId: number, esteiraId: number, silencioso = false) => {
    setSincronizando(true);
    setSyncError('');
    try {
      const res = await workflowService.sincronizarEcd(tenantId, esteiraId);
      const { contas: c } = await carregarDadosDeMapeamento(tenantId);
      // A importação do ECD só traz contas; nenhum vínculo De/Para é criado aqui.
      if (res.inseridas > 0 || !silencioso) {
        showToast(res.inseridas > 0
          ? `ECD importado: ${res.inseridas} contas adicionadas, ${res.atualizadas} atualizadas.`
          : `Re-sync concluído: ${res.atualizadas} contas atualizadas.`);
      }
      if (c.length === 0 && silencioso) {
        setSyncError('Nenhuma conta encontrada no ECD para este período. Verifique se o ECD foi importado corretamente.');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Erro desconhecido';
      setSyncError(`Erro ao sincronizar do ECD: ${msg}`);
    } finally {
      setSincronizando(false);
    }
  };

  // ── Regra Depreciação/Amortização: só por ação do usuário ─────────────────
  const [aplicandoRegra, setAplicandoRegra] = useState(false);
  const vinculosDaRegra = contarVinculosDaRegra(mapeamentos);

  const preVincularDepreciacao = async () => {
    if (!selectedEmpresa) return;
    const confirmacao = await Swal.fire({
      icon: 'question',
      title: 'Pré-vincular Depreciação e Amortização?',
      html: 'As contas analíticas de <b>depreciação</b> e <b>amortização</b> que ainda <b>não têm mapeamento</b> serão vinculadas ' +
        'ao grupo <b>Imobilizado</b>, nos subgrupos Depreciação e Amortização. Contas de resultado e contas já mapeadas não são tocadas. ' +
        'Os vínculos criados ficam identificados como "regra" e podem ser desfeitos.',
      showCancelButton: true, confirmButtonText: 'Pré-vincular', cancelButtonText: 'Cancelar',
    });
    if (!confirmacao.isConfirmed) return;
    try {
      setAplicandoRegra(true);
      const { preVinculadas } = await workflowService.deParaPreVincular(selectedEmpresa.id);
      await carregarDadosDeMapeamento(selectedEmpresa.id);
      showToast(preVinculadas > 0
        ? `${preVinculadas} conta(s) de depreciação/amortização pré-vinculada(s) ao Imobilizado.`
        : 'Nenhuma conta nova para pré-vincular: as elegíveis já estão mapeadas.');
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro ao pré-vincular.', text: err?.response?.data?.message });
    } finally {
      setAplicandoRegra(false);
    }
  };

  const desfazerPreVinculos = async () => {
    if (!selectedEmpresa) return;
    const confirmacao = await Swal.fire({
      icon: 'warning',
      title: `Desfazer ${vinculosDaRegra} pré-vinculação(ões)?`,
      text: 'Só os vínculos criados pela regra de Depreciação/Amortização serão removidos. Mapeamentos feitos por pessoas continuam como estão.',
      showCancelButton: true, confirmButtonText: 'Desfazer', cancelButtonText: 'Cancelar', confirmButtonColor: '#f64e60',
    });
    if (!confirmacao.isConfirmed) return;
    try {
      setAplicandoRegra(true);
      const { removidas } = await workflowService.deParaDesfazerPreVinculos(selectedEmpresa.id);
      await carregarDadosDeMapeamento(selectedEmpresa.id);
      showToast(`${removidas} pré-vinculação(ões) desfeita(s).`);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro ao desfazer as pré-vinculações.', text: err?.response?.data?.message });
    } finally {
      setAplicandoRegra(false);
    }
  };

  const getMapeamento = (contaId: number) => mapeamentos.find(m => m.contaId === contaId);

  // Categorias únicas do ECD (campo grupo_conta)
  // "Depreciação" é sempre oferecida no filtro: são contas que ficam dentro de Imobilizado.
  const categoriasEcd = Array.from(
    new Set([...(contas.map(c => c.grupoConta).filter(Boolean) as string[]), GRUPO_DEPRECIACAO])
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  // Grupos já mapeados (para autocomplete)
  const gruposDistintos = Array.from(new Set(gruposExistentes.map(g => g.grupoNome)));
  // Datalist dos formulários: predefinidos (inclui Depreciação) + os já criados nesta empresa
  const opcoesGrupoNome = opcoesDeGrupo(gruposDistintos);
  // Subgrupos: predefinidos (Depreciação, Amortização) + os já usados nesta empresa
  const opcoesSubgrupoNome = opcoesDeSubgrupo(gruposExistentes.map(g => g.subgrupoNome));

  const contasFiltradas = contas.filter(c => {
    const q = busca.toLowerCase();
    if (q && !c.codigo.toLowerCase().includes(q) && !c.descricao.toLowerCase().includes(q)) return false;
    if (filtroCategoria === GRUPO_DEPRECIACAO) {
      if (!ehContaDeDepreciacao(c.descricao)) return false;
    } else if (filtroCategoria && c.grupoConta !== filtroCategoria) return false;
    if (filtroTipo !== 'todos' && c.tipo !== filtroTipo) return false;
    if (tipoGrupo === 'CLIENTES' && !ehContaDeCliente(c.grupoConta)) return false;
    if (filtroStatus !== 'todos') {
      const mapeado = !!getMapeamento(c.contaId);
      if (filtroStatus === 'mapeado' && !mapeado) return false;
      if (filtroStatus === 'pendente' && mapeado) return false;
    }
    return true;
  });

  const POR_PAGINA = 20;
  const totalPaginas = Math.max(1, Math.ceil(contasFiltradas.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas - 1);
  const contasPagina = contasFiltradas.slice(paginaAtual * POR_PAGINA, (paginaAtual + 1) * POR_PAGINA);

  const filtrosAtivos = !!(busca || filtroCategoria || filtroStatus !== 'todos' || filtroTipo !== 'todos');

  // ── Seleção por flag (checkbox) ───────────────────────────────────────────
  const todasVisiveisSelecionadas =
    contasFiltradas.length > 0 && contasFiltradas.every(c => selecionadas.has(c.contaId));

  const alternarConta = (contaId: number) =>
    setSelecionadas(prev => {
      const next = new Set(prev);
      next.has(contaId) ? next.delete(contaId) : next.add(contaId);
      return next;
    });

  const alternarTodasVisiveis = () =>
    setSelecionadas(prev => {
      const next = new Set(prev);
      if (todasVisiveisSelecionadas) contasFiltradas.forEach(c => next.delete(c.contaId));
      else contasFiltradas.forEach(c => next.add(c.contaId));
      return next;
    });

  const alterarTipoGrupo = (tipo: 'QUALQUER' | 'CLIENTES') => {
    setTipoGrupo(tipo);
    setPagina(0);
    // Ao restringir a Clientes, descarta da seleção qualquer conta que não seja de Cliente
    if (tipo === 'CLIENTES') {
      setSelecionadas(prev => {
        const permitidas = new Set(contas.filter(c => ehContaDeCliente(c.grupoConta)).map(c => c.contaId));
        return new Set(Array.from(prev).filter(id => permitidas.has(id)));
      });
    }
  };

  // Grupos De/Para já criados, com as contas vinculadas a cada um
  const gruposComContas = Array.from(
    mapeamentos.reduce((acc, m) => {
      if (!acc.has(m.grupoNome)) acc.set(m.grupoNome, []);
      acc.get(m.grupoNome)!.push(m);
      return acc;
    }, new Map<string, DeParaItem[]>())
  ).sort((a, b) => a[0].localeCompare(b[0]));

  const limparFiltros = () => {
    setBusca('');
    setFiltroCategoria('');
    setFiltroStatus('todos');
    setFiltroTipo('todos');
    setShowLote(false);
    setPagina(0);
  };

  // ── Form individual ───────────────────────────────────────────────────────
  const abrirForm = (conta: MatrizContaContabil) => {
    const existente = getMapeamento(conta.contaId);
    setContaSelecionada(conta);
    setGrupoNome(existente?.grupoNome ?? conta.grupoConta ?? '');
    setSubgrupoNome(existente?.subgrupoNome ?? '');
    justificativa.restaurar();
    setShowForm(true);
  };

  const fecharForm = () => {
    setShowForm(false);
    setContaSelecionada(null);
    setGrupoNome('');
    setSubgrupoNome('');
    justificativa.restaurar();
  };

  const salvar = async () => {
    if (!contaSelecionada || !selectedEmpresa) return;
    if (!grupoNome.trim()) { Swal.fire({ icon: 'warning', title: 'Grupo é obrigatório.' }); return; }
    if (!justificativa.texto.trim()) { Swal.fire({ icon: 'warning', title: 'Justificativa SOX é obrigatória.' }); return; }
    try {
      setSalvando(true);
      await workflowService.deParaSalvar({
        tenantId: selectedEmpresa.id,
        contaId: contaSelecionada.contaId,
        contaCodigo: contaSelecionada.codigo,
        contaDescricao: contaSelecionada.descricao,
        grupoNome: grupoNome.trim(),
        subgrupoNome: subgrupoNome.trim() || undefined,
        justificativaSox: justificativa.texto.trim(),
        criadoPorId: userId,
      });
      if (selectedPeriodo) await carregarContas(selectedEmpresa.id, selectedPeriodo.esteiraId);
      fecharForm();
      showToast('Mapeamento salvo. Registro SOX gerado.');
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro ao salvar o mapeamento.', text: err?.response?.data?.message });
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (grupoContaId: number, codigo: string) => {
    const confirm = await Swal.fire({
      title: `Remover mapeamento da conta ${codigo}?`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Remover', cancelButtonText: 'Cancelar',
      confirmButtonColor: '#f64e60',
    });
    if (!confirm.isConfirmed) return;
    try {
      await workflowService.deParaRemover(grupoContaId);
      if (selectedEmpresa && selectedPeriodo) await carregarContas(selectedEmpresa.id, selectedPeriodo.esteiraId);
      showToast('Mapeamento removido.');
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao remover.' });
    }
  };

  // ── Mapeamento em lote ────────────────────────────────────────────────────
  const salvarEmLote = async () => {
    if (!selectedEmpresa) return;
    if (!grupoNomeLote.trim()) { Swal.fire({ icon: 'warning', title: 'Grupo é obrigatório.' }); return; }
    if (!justificativaLote.texto.trim()) { Swal.fire({ icon: 'warning', title: 'Justificativa SOX é obrigatória.' }); return; }

    const contaIds = Array.from(selecionadas);
    if (contaIds.length === 0) { Swal.fire({ icon: 'warning', title: 'Marque ao menos uma conta (flag) para criar o grupo.' }); return; }
    if (tipoGrupo === 'CLIENTES') {
      const indevidas = contas.filter(c => selecionadas.has(c.contaId) && !ehContaDeCliente(c.grupoConta));
      if (indevidas.length > 0) {
        Swal.fire({ icon: 'error', title: 'Grupo de Clientes aceita apenas contas de Clientes.', text: indevidas.slice(0, 5).map(c => c.codigo).join(', ') });
        return;
      }
    }

    try {
      setSalvandoLote(true);
      const res = await workflowService.deParaSalvarEmLote({
        tenantId: selectedEmpresa.id,
        contaIds,
        grupoNome: grupoNomeLote.trim(),
        subgrupoNome: subgrupoNomeLote.trim() || undefined,
        justificativaSox: justificativaLote.texto.trim(),
        criadoPorId: userId,
        tipoGrupo: tipoGrupo === 'CLIENTES' ? 'CLIENTES' : undefined,
      });
      if (selectedPeriodo) await carregarContas(selectedEmpresa.id, selectedPeriodo.esteiraId);
      setSelecionadas(new Set());
      setShowLote(false);
      setGrupoNomeLote('');
      setSubgrupoNomeLote('');
      justificativaLote.restaurar();
      showToast(`${res.mapeadas} contas mapeadas para "${grupoNomeLote}". Registro SOX gerado.`);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro ao mapear em lote.', text: err?.response?.data?.message });
    } finally {
      setSalvandoLote(false);
    }
  };

  // ── Editar a composição de contas de um grupo ─────────────────────────────
  // O tipo "Clientes" não é gravado no grupo; é inferido: se todas as contas atuais são de Cliente,
  // a edição continua restrita a contas de Cliente.
  const grupoEmEdicaoItens = grupoEmEdicao ? gruposComContas.find(([nome]) => nome === grupoEmEdicao)?.[1] ?? [] : [];
  const grupoEmEdicaoEhClientes = grupoEmEdicaoItens.length > 0 && grupoEmEdicaoItens.every(i =>
    ehContaDeCliente(contas.find(c => c.contaId === i.contaId)?.grupoConta));
  const grupoPorConta = new Map(mapeamentos.map(m => [m.contaId, m.grupoNome] as [number, string]));

  const salvarComposicao = async (contaIds: number[], justificativaSox: string) => {
    if (!selectedEmpresa || !grupoEmEdicao) return;
    try {
      setSalvandoComposicao(true);
      const r = await workflowService.deParaSalvarComposicao({
        tenantId: selectedEmpresa.id,
        grupoNome: grupoEmEdicao,
        contaIds,
        justificativaSox,
        criadoPorId: userId,
        tipoGrupo: grupoEmEdicaoEhClientes ? 'CLIENTES' : undefined,
      });
      await carregarDadosDeMapeamento(selectedEmpresa.id);
      setGrupoEmEdicao(null);
      showToast(`Grupo "${grupoEmEdicao}" atualizado: ${r.adicionadas} incluída(s), ${r.removidas} removida(s).`);
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Erro ao salvar a composição do grupo.', text: err?.response?.data?.message });
    } finally {
      setSalvandoComposicao(false);
    }
  };

  // ── Editar / excluir grupo ────────────────────────────────────────────────
  const removerGrupo = async (nome: string, qtd: number) => {
    if (!selectedEmpresa) return;
    const confirm = await Swal.fire({
      title: `Excluir o grupo "${nome}"?`,
      text: `${qtd} conta(s) deixarão de pertencer ao grupo (as contas em si não são apagadas).`,
      icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir grupo', cancelButtonText: 'Cancelar', confirmButtonColor: '#f64e60',
    });
    if (!confirm.isConfirmed) return;
    try {
      await workflowService.deParaRemoverGrupo(selectedEmpresa.id, nome);
      if (selectedPeriodo) await carregarContas(selectedEmpresa.id, selectedPeriodo.esteiraId);
      showToast(`Grupo "${nome}" excluído.`);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao excluir o grupo.' });
    }
  };

  const editarGrupo = async (nome: string) => {
    if (!selectedEmpresa) return;
    const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const { value } = await Swal.fire({
      title: `Renomear grupo "${nome}"`,
      html:
        `<input id="swal-grupo" class="swal2-input" placeholder="Nome do grupo" value="${esc(nome)}">` +
        `<textarea id="swal-just" class="swal2-textarea" placeholder="Justificativa SOX (obrigatória)" rows="4">${esc(justificativaRenomearGrupo(nome))}</textarea>`,
      focusConfirm: false, showCancelButton: true, confirmButtonText: 'Salvar', cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const g = (document.getElementById('swal-grupo') as HTMLInputElement).value.trim();
        const j = (document.getElementById('swal-just') as HTMLTextAreaElement).value.trim();
        if (!g) { Swal.showValidationMessage('Nome do grupo é obrigatório.'); return; }
        if (!j) { Swal.showValidationMessage('Justificativa SOX é obrigatória.'); return; }
        return { g, j };
      },
    });
    if (!value) return;
    try {
      await workflowService.deParaRenomearGrupo({
        tenantId: selectedEmpresa.id, grupoNomeAtual: nome, grupoNomeNovo: value.g,
        justificativaSox: value.j,
      });
      if (selectedPeriodo) await carregarContas(selectedEmpresa.id, selectedPeriodo.esteiraId);
      showToast(`Grupo atualizado para "${value.g}".`);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao editar o grupo.' });
    }
  };

  // ── Indicadores ───────────────────────────────────────────────────────────
  const totalMapeadas = mapeamentos.length;
  const totalContas = contas.filter(c => c.tipo === 'ANALITICA').length;
  const { percentual: cobertura, pendentes: totalPendentes } = calcularCobertura(totalMapeadas, totalContas);
  const isReady = !!selectedEmpresa && !!selectedPeriodo;

  return (
    <div className='wf-page'>
      <div className='wf-header'>
        <div>
          <h1 className='wf-header__title'>De/Para — Hierarquia de Grupos de Contas</h1>
          <p className='wf-header__subtitle'>
            Mapeie as contas ECD para os grupos e subgrupos do seu plano de negócio.
            Usado como base para a Matriz de Aprovadores SOX.
          </p>
        </div>
        <button className='wf-btn wf-btn--secondary' onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: 1 })}>
          ← Onboarding Etapa 1
        </button>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      {/* Empresa + Período */}
      <div className='wf-card' style={{ marginBottom: 16, padding: '20px 24px' }}>
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 300px', minWidth: 240 }}>
            <label style={labelStyle}>Empresa</label>
            <select
              value={selectedEmpresa?.id ?? ''}
              onChange={e => {
                const emp = empresas.find(x => x.id === Number(e.target.value));
                if (emp) selecionarEmpresa(emp);
                else { setSelectedEmpresa(null); setPeriodos([]); setSelectedPeriodo(null); setContas([]); }
              }}
              style={selectStyle}
            >
              <option value=''>
                {empresasConfiguradas.length === 0 ? 'Nenhuma empresa configurada no Grupo Econômico' : '— Selecione uma empresa —'}
              </option>
              {empresasConfiguradas.map(e => (
                <option key={e.id} value={e.id}>{e.nomeFantasia || e.razaoSocial || `Empresa #${e.id}`}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
            <label style={labelStyle}>Período</label>
            <select
              value={selectedPeriodo?.esteiraId ?? ''}
              onChange={e => {
                const p = periodos.find(x => x.esteiraId === Number(e.target.value));
                if (p && selectedEmpresa) selecionarPeriodo(selectedEmpresa, p);
                else setSelectedPeriodo(null);
              }}
              disabled={!selectedEmpresa || periodos.length === 0}
              style={selectStyle}
            >
              <option value=''>
                {!selectedEmpresa ? '— Selecione a empresa primeiro —'
                  : periodos.length === 0 ? 'Sem períodos importados'
                  : '— Selecione um período —'}
              </option>
              {periodos.map(p => (
                <option key={p.esteiraId} value={p.esteiraId}>
                  {fmt(p.periodoInicio)} – {fmt(p.periodoFim)}
                </option>
              ))}
            </select>
          </div>

          {isReady && contas.length > 0 && (
            <div style={{ flex: '0 0 auto' }}>
              <label style={{ ...labelStyle, visibility: 'hidden' }}>.</label>
              <button
                className='wf-btn wf-btn--secondary'
                onClick={() => selectedEmpresa && selectedPeriodo && sincronizarDoEcd(selectedEmpresa.id, selectedPeriodo.esteiraId)}
                disabled={sincronizando}
                title='Re-sincronizar quando o ECD for atualizado'
              >
                {sincronizando ? '↻ Atualizando...' : '↻ Atualizar ECD'}
              </button>
            </div>
          )}
          {isReady && contas.length > 0 && (
            <div style={{ flex: '0 0 auto' }}>
              <label style={{ ...labelStyle, visibility: 'hidden' }}>.</label>
              <button
                className='wf-btn wf-btn--secondary'
                onClick={preVincularDepreciacao}
                disabled={aplicandoRegra}
                title='Vincula ao Imobilizado (subgrupos Depreciação e Amortização) as contas elegíveis ainda sem mapeamento'
              >
                Pré-vincular Depreciação e Amortização
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Indicadores */}
      <div className='wf-metrics' style={{ marginBottom: 20 }}>
        {[
          ['Total de contas analíticas', String(totalContas), 'importadas via ECD', 'primary'],
          ['Contas mapeadas', String(totalMapeadas), `${cobertura}% de cobertura De/Para`, cobertura === 100 ? 'success' : cobertura >= 50 ? 'warning' : 'danger'],
          ['Grupos distintos', String(gruposDistintos.length), 'grupos de negócio configurados', 'info'],
          ['Pendentes', String(totalPendentes), 'aguardam mapeamento', 'warning'],
        ].map(([titulo, valor, detalhe, cor]) => (
          <div key={titulo} className={`wf-metric wf-metric--${cor}`}>
            <span>{titulo}</span><strong>{valor}</strong><small>{detalhe}</small>
          </div>
        ))}
      </div>

      <div className='wf-cobertura' data-testid='cobertura-depara' style={{ marginBottom: 20 }}>
        <div className='wf-cobertura__topo'>
          <span>Cobertura De/Para · {totalMapeadas} de {totalContas} contas mapeadas</span>
          <strong>{cobertura}%</strong>
        </div>
        <div className='wf-cobertura__trilho' role='progressbar' aria-label='Cobertura De/Para'
          aria-valuemin={0} aria-valuemax={100} aria-valuenow={cobertura}>
          <div className={`wf-cobertura__barra wf-cobertura__barra--${cobertura === 100 ? 'success' : cobertura >= 50 ? 'warning' : 'danger'}`}
            style={{ width: `${cobertura}%` }} />
        </div>
      </div>

      <section className='wf-card'>
        {/* Filtros */}
        <div style={{ borderBottom: '1px solid #f1f1f2', paddingBottom: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '2 1 200px', minWidth: 180 }}>
              <label style={filterLabel}>Buscar conta</label>
              <input
                value={busca}
                onChange={e => { setBusca(e.target.value); setPagina(0); }}
                placeholder='Código ou descrição...'
                disabled={!isReady}
                style={filterInput}
              />
            </div>

            <div style={{ flex: '1 1 160px', minWidth: 140 }}>
              <label style={filterLabel}>Grupo</label>
              <select
                value={filtroCategoria}
                onChange={e => { setFiltroCategoria(e.target.value); setShowLote(false); setPagina(0); }}
                disabled={!isReady || categoriasEcd.length === 0}
                style={filterSelect}
              >
                <option value=''>Todos os grupos</option>
                {categoriasEcd.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ flex: '1 1 170px', minWidth: 150 }}>
              <label style={filterLabel}>Tipo de grupo De/Para</label>
              <select value={tipoGrupo} onChange={e => alterarTipoGrupo(e.target.value as 'QUALQUER' | 'CLIENTES')} disabled={!isReady} style={filterSelect}>
                <option value='QUALQUER'>Todas as contas</option>
                <option value='CLIENTES'>Somente Clientes</option>
              </select>
            </div>

            <div style={{ flex: '0 1 120px', minWidth: 100 }}>
              <label style={filterLabel}>Tipo</label>
              <select value={filtroTipo} onChange={e => { setFiltroTipo(e.target.value as any); setPagina(0); }} disabled={!isReady} style={filterSelect}>
                <option value='todos'>Todos</option>
                <option value='ANALITICA'>Analítica</option>
                <option value='SINTETICA'>Sintética</option>
              </select>
            </div>

            <div style={{ flex: '0 1 110px', minWidth: 90 }}>
              <label style={filterLabel}>Status</label>
              <select value={filtroStatus} onChange={e => { setFiltroStatus(e.target.value as any); setPagina(0); }} disabled={!isReady} style={filterSelect}>
                <option value='todos'>Todos</option>
                <option value='mapeado'>Mapeadas</option>
                <option value='pendente'>Pendentes</option>
              </select>
            </div>

            {filtrosAtivos && (
              <div style={{ flex: '0 0 auto', paddingBottom: 1 }}>
                <label style={{ ...filterLabel, visibility: 'hidden' }}>.</label>
                <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={limparFiltros}>✕ Limpar</button>
              </div>
            )}

            {/* Botão mapeamento em lote — aparece sempre que há contas visíveis */}
            {isReady && contasFiltradas.length > 0 && (
              <div style={{ flex: '0 0 auto', marginLeft: 'auto', paddingBottom: 1 }}>
                <label style={{ ...filterLabel, visibility: 'hidden' }}>.</label>
                <button
                  className='wf-btn wf-btn--primary'
                  onClick={() => setShowLote(v => !v)}
                >
                  {showLote ? '✕ Fechar' : `Criar Grupo De/Para (${selecionadas.size} selecionada${selecionadas.size === 1 ? '' : 's'})`}
                </button>
              </div>
            )}
          </div>

          {filtrosAtivos && contas.length > 0 && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#7e8299' }}>
              Exibindo {contasFiltradas.length} de {contas.length} contas
            </div>
          )}
        </div>

        {/* Painel de mapeamento em lote */}
        {showLote && (
          <div style={{
            background: '#f8f9fa', border: '1px solid #e4e6ef', borderRadius: 10,
            padding: '20px 24px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#181c32' }}>Mapeamento em lote</span>
              <span style={{
                fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                background: '#e8f4fb', color: '#009ef7',
              }}>
                {selecionadas.size} conta(s) marcada(s){tipoGrupo === 'CLIENTES' ? ' · Somente Clientes' : ''}
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#5e6278' }}>
                Grupo De/Para <span style={{ color: '#f64e60', fontWeight: 400, fontSize: 11 }}>*obrigatório</span>
                <input
                  value={grupoNomeLote}
                  onChange={e => setGrupoNomeLote(e.target.value)}
                  placeholder='Ex: BANCO E CAIXA, TRIBUTOS A RECOLHER'
                  list='grupos-lote'
                  style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e4e6ef', fontSize: 13 }}
                />
                <datalist id='grupos-lote'>
                  {opcoesGrupoNome.map(nome => <option key={nome} value={nome} />)}
                </datalist>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#5e6278' }}>
                Subgrupo <span style={{ color: '#a1a5b7', fontWeight: 400, fontSize: 11 }}>opcional</span>
                <input
                  value={subgrupoNomeLote}
                  onChange={e => setSubgrupoNomeLote(e.target.value)}
                  placeholder='Ex: Caixa, Bancos Nacionais'
                  list='subgrupos-lote'
                  style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e4e6ef', fontSize: 13 }}
                />
                <datalist id='subgrupos-lote'>
                  {opcoesSubgrupoNome.map(nome => <option key={nome} value={nome} />)}
                </datalist>
              </label>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 600, color: '#5e6278', marginBottom: 16 }}>
              Justificativa SOX <span style={{ color: '#f64e60', fontWeight: 400, fontSize: 11 }}>*obrigatória</span>
              <CampoJustificativaSox
                justificativa={justificativaLote}
                rows={4}
                placeholder='Descreva o critério de classificação das contas desta categoria no grupo selecionado...'
                style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #e4e6ef', fontSize: 13, resize: 'vertical' }}
              />
            </label>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <button className='wf-btn wf-btn--primary' onClick={salvarEmLote} disabled={salvandoLote}>
                {salvandoLote ? 'Criando grupo...' : `Criar grupo com ${selecionadas.size} conta(s) →`}
              </button>
              <button className='wf-btn wf-btn--secondary' onClick={() => setShowLote(false)}>Cancelar</button>
              <span style={{ fontSize: 11, color: '#a1a5b7', marginLeft: 4 }}>
                🔒 Registro SOX com hash SHA-256 será gerado.
              </span>
            </div>
          </div>
        )}

        {/* Error state */}
        {syncError && (
          <div style={{
            background: '#fff5f8', border: '1px solid #f64e60', borderRadius: 8,
            padding: '12px 16px', marginBottom: 16, color: '#f64e60', fontSize: 13,
          }}>
            <strong>Atenção:</strong> {syncError}
          </div>
        )}

        {/* Estados */}
        {!selectedEmpresa ? (
          <div style={emptyStyle}>Selecione uma empresa para começar.</div>
        ) : !selectedPeriodo ? (
          <div style={emptyStyle}>Selecione o período do ECD acima.</div>
        ) : loading || sincronizando ? (
          <div style={emptyStyle}>{sincronizando ? '↻ Importando contas do ECD...' : 'Carregando contas...'}</div>
        ) : (
          <div className='wf-table-wrap'>
            <table className='wf-table'>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type='checkbox' checked={todasVisiveisSelecionadas} onChange={alternarTodasVisiveis}
                      title='Marcar todas as contas filtradas' />
                  </th>
                  <th>Código</th>
                  <th>Descrição da conta</th>
                  <th>Grupo</th>
                  <th>Tipo</th>
                  <th>Grupo (De/Para)</th>
                  <th>Subgrupo</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {contasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', color: '#a1a5b7', padding: 24 }}>
                      {filtrosAtivos
                        ? 'Nenhuma conta para os filtros selecionados.'
                        : 'Nenhuma conta disponível para esta empresa / período.'}
                    </td>
                  </tr>
                ) : contasPagina.map(conta => {
                  const map = getMapeamento(conta.contaId);
                  return (
                    <tr key={conta.contaId}>
                      <td>
                        <input type='checkbox' checked={selecionadas.has(conta.contaId)} onChange={() => alternarConta(conta.contaId)} />
                      </td>
                      <td><code style={{ fontSize: 12 }}>{conta.codigo}</code></td>
                      <td>
                        <strong>{conta.descricao}</strong>
                        {conta.codigoReduzido && (
                          <span style={{ display: 'block', fontSize: 11, color: '#a1a5b7' }}>
                            Cód. reduzido: {conta.codigoReduzido}
                          </span>
                        )}
                      </td>
                      <td>
                        {conta.grupoConta
                          ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#f4f4f4', color: '#7e8299' }}>{conta.grupoConta}</span>
                          : <span style={{ color: '#a1a5b7' }}>—</span>}
                      </td>
                      <td>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 10,
                          background: conta.tipo === 'ANALITICA' ? '#e8f4fb' : '#f4f4f4',
                          color: conta.tipo === 'ANALITICA' ? '#009ef7' : '#7e8299',
                        }}>
                          {conta.tipo}
                        </span>
                      </td>
                      <td>{map ? <strong style={{ color: '#181c32' }}>{map.grupoNome}</strong> : <span style={{ color: '#a1a5b7' }}>—</span>}</td>
                      <td>{map?.subgrupoNome ?? <span style={{ color: '#a1a5b7' }}>—</span>}</td>
                      <td>
                        {map
                          ? <>
                              <span style={{ color: '#50cd89', fontWeight: 600, fontSize: 12 }}>✓ Mapeado</span>
                              {ehVinculoDaRegra(map) && (
                                <span title={DICA_REGRA} style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#fff8e1', color: '#7c5c00', border: '1px solid #f0d58c' }}>
                                  {ROTULO_REGRA}
                                </span>
                              )}
                            </>
                          : <span style={{ color: '#f64e60', fontWeight: 600, fontSize: 12 }}>Pendente</span>}
                      </td>
                      <td className='wf-table-actions'>
                        <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={() => abrirForm(conta)}>
                          {map ? 'Editar' : 'Mapear'}
                        </button>
                        {map && (
                          <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => remover(map.grupoContaId, conta.codigo)}>
                            Remover
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Paginação */}
            {contasFiltradas.length > POR_PAGINA && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 4px', marginTop: 4,
              }}>
                <span style={{ fontSize: 12, color: '#7e8299' }}>
                  Exibindo {paginaAtual * POR_PAGINA + 1}–{Math.min((paginaAtual + 1) * POR_PAGINA, contasFiltradas.length)} de {contasFiltradas.length} contas
                </span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={() => setPagina(p => Math.max(0, p - 1))}
                    disabled={paginaAtual === 0}
                    style={pgBtn}
                  >‹ Anterior</button>
                  {Array.from({ length: totalPaginas }, (_, i) => i)
                    .filter(i => Math.abs(i - paginaAtual) <= 2)
                    .map(i => (
                      <button key={i} onClick={() => setPagina(i)} style={{
                        ...pgBtn,
                        background: i === paginaAtual ? '#009ef7' : '#fff',
                        color: i === paginaAtual ? '#fff' : '#181c32',
                        fontWeight: i === paginaAtual ? 700 : 400,
                        borderColor: i === paginaAtual ? '#009ef7' : '#e4e6ef',
                      }}>{i + 1}</button>
                    ))
                  }
                  <button
                    onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))}
                    disabled={paginaAtual >= totalPaginas - 1}
                    style={pgBtn}
                  >Próxima ›</button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      {isReady && vinculosDaRegra > 0 && (
        <div className='wf-alert-banner' role='status' style={{ marginTop: 16 }}>
          <strong>{vinculosDaRegra} vínculo(s) criado(s) pela regra de Depreciação/Amortização</strong>
          <p style={{ margin: '4px 0 8px', fontSize: 12 }}>
            Estas contas foram vinculadas pela regra de pré-vinculação, não por um mapeamento manual. Revise abaixo (elas têm o selo
            "{ROTULO_REGRA}") ou desfaça: mapeamentos feitos por pessoas não são afetados.
          </p>
          <button className='wf-btn wf-btn--secondary wf-btn--sm' disabled={aplicandoRegra} onClick={desfazerPreVinculos}>
            Desfazer pré-vinculações ({vinculosDaRegra})
          </button>
        </div>
      )}

      {/* Grupos De/Para criados — visualizar contas vinculadas, editar e excluir */}
      {isReady && gruposComContas.length > 0 && (
        <section className='wf-card' style={{ marginTop: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Grupos De/Para criados ({gruposComContas.length})</h3>
          {gruposComContas.map(([nome, itens]) => {
            const aberto = gruposAbertos.has(nome);
            return (
              <div key={nome} style={{ border: '1px solid #e4e6ef', borderRadius: 8, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
                  <button
                    className='wf-link'
                    onClick={() => setGruposAbertos(prev => {
                      const next = new Set(prev);
                      next.has(nome) ? next.delete(nome) : next.add(nome);
                      return next;
                    })}
                  >
                    {aberto ? '▼' : '▶'}
                  </button>
                  <strong style={{ flex: 1 }}>
                    {nome}
                  </strong>
                  <span style={{ fontSize: 12, color: '#7e8299' }}>{itens.length} conta(s)</span>
                  <button className='wf-btn wf-btn--primary wf-btn--sm' onClick={() => setGrupoEmEdicao(nome)}>Editar contas</button>
                  <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => editarGrupo(nome)}>Renomear</button>
                  <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => removerGrupo(nome, itens.length)}>Excluir</button>
                </div>
                {aberto && (
                  <div style={{ padding: '0 14px 10px 40px', fontSize: 13 }}>
                    {agruparPorSubgrupo(itens).map(({ subgrupo, itens: contasDoSubgrupo }) => (
                      <div key={subgrupo ?? '__sem_subgrupo__'} data-testid='subgrupo' style={{ marginBottom: 8 }}>
                        <div style={{ fontWeight: 600, color: '#3f4254', padding: '4px 0' }}>
                          {subgrupo ?? 'Sem subgrupo'}
                          <span style={{ fontWeight: 400, color: '#7e8299' }}> · {contasDoSubgrupo.length} conta(s)</span>
                        </div>
                        {contasDoSubgrupo.map(i => (
                          <div key={i.grupoContaId} style={{ display: 'flex', gap: 8, padding: '3px 0 3px 14px' }}>
                            <code>{i.contaCodigo}</code>
                            <span style={{ flex: 1 }}>
                              {i.contaDescricao}
                              {ehVinculoDaRegra(i) && (
                                <span title={DICA_REGRA} style={{ marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 8, background: '#fff8e1', color: '#7c5c00', border: '1px solid #f0d58c' }}>
                                  {ROTULO_REGRA}
                                </span>
                              )}
                            </span>
                            <button className='wf-link' onClick={() => remover(i.grupoContaId, i.contaCodigo)}>remover conta</button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}

      {grupoEmEdicao && (
        <EditarComposicaoGrupoModal
          grupoNome={grupoEmEdicao}
          contasDoGrupo={grupoEmEdicaoItens}
          contasElegiveis={contas}
          grupoPorConta={grupoPorConta}
          apenasClientes={grupoEmEdicaoEhClientes}
          podeSerCliente={c => ehContaDeCliente(c.grupoConta)}
          salvando={salvandoComposicao}
          onSalvar={salvarComposicao}
          onFechar={() => setGrupoEmEdicao(null)}
        />
      )}

      {/* Modal individual */}
      {showForm && contaSelecionada && (
        <div className='wf-modal-bg'>
          <div className='wf-modal'>
            <div className='wf-modal__header'>
              <div>
                <h2>Mapear conta — De/Para SOX</h2>
                <p><code>{contaSelecionada.codigo}</code> — {contaSelecionada.descricao}</p>
              </div>
              <button onClick={fecharForm}>×</button>
            </div>
            <div className='wf-modal__body'>
              <div className='wf-form-grid'>
                <label style={{ gridColumn: '1 / -1' }}>
                  Grupo de negócio (De/Para)
                  <input value={grupoNome} onChange={e => setGrupoNome(e.target.value)}
                    placeholder='Ex: ATIVO CIRCULANTE, TRIBUTOS' list='grupos-existentes' />
                  <datalist id='grupos-existentes'>
                    {opcoesGrupoNome.map(nome => <option key={nome} value={nome} />)}
                  </datalist>
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Subgrupo (opcional)
                  <input value={subgrupoNome} onChange={e => setSubgrupoNome(e.target.value)}
                    placeholder='Ex: Caixa e Equivalentes' list='subgrupos-existentes' />
                  <datalist id='subgrupos-existentes'>
                    {opcoesSubgrupoNome.map(nome => <option key={nome} value={nome} />)}
                  </datalist>
                </label>
                <label style={{ gridColumn: '1 / -1' }}>
                  Justificativa SOX (obrigatória)
                  <CampoJustificativaSox justificativa={justificativa} rows={4} placeholder='Descreva o critério de classificação...' />
                </label>
              </div>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#7e8299', marginTop: 8 }}>
                🔒 Este mapeamento será registrado no log imutável de Auditoria SOX com hash SHA-256.
              </div>
            </div>
            <div className='wf-modal__footer'>
              <button className='wf-btn wf-btn--secondary' onClick={fecharForm}>Cancelar</button>
              <button className='wf-btn wf-btn--primary' onClick={salvar} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar mapeamento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontWeight: 600, fontSize: 13, color: '#5e6278', marginBottom: 6,
};
const selectStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 6,
  border: '1px solid #e4e6ef', fontSize: 14, color: '#181c32', background: '#fff',
};
const filterLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: '#a1a5b7', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
};
const filterInput: React.CSSProperties = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid #e4e6ef', fontSize: 13, color: '#181c32', background: '#fff',
};
const filterSelect: React.CSSProperties = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid #e4e6ef', fontSize: 13, color: '#181c32', background: '#fff',
};
const emptyStyle: React.CSSProperties = {
  textAlign: 'center', padding: 40, color: '#a1a5b7', fontSize: 14,
};
const pgBtn: React.CSSProperties = {
  padding: '5px 12px', borderRadius: 6, border: '1px solid #e4e6ef',
  background: '#fff', color: '#181c32', fontSize: 13, cursor: 'pointer',
};

export default CadastroDeParaPage;
